import React, { useEffect } from "react";
import { AdvertiseBanner } from "../features/advertise/api/types";

interface PopupState {
  viewCount: number;
  lastShownAt: string | null;
}

const POPUP_COOLDOWN_MS = 48 * 60 * 60 * 1000; // 48 hours
const MAX_VIEW_COUNT = 3;

function getPopupStateKey(id: string): string {
  return `popup_state_${id}`;
}

function readPopupState(id: string): PopupState {
  try {
    const raw = localStorage.getItem(getPopupStateKey(id));
    if (raw) {
      return JSON.parse(raw) as PopupState;
    }
  } catch {
    // Ignore parse errors — start fresh.
  }
  return { viewCount: 0, lastShownAt: null };
}

function writePopupState(id: string, state: PopupState): void {
  try {
    localStorage.setItem(getPopupStateKey(id), JSON.stringify(state));
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.).
  }
}

/** Returns true when the popup with the given state should be shown to the user. */
export function isPopupEligible(state: PopupState): boolean {
  if (state.viewCount >= MAX_VIEW_COUNT) {
    return false;
  }
  if (state.lastShownAt !== null) {
    const elapsed = Date.now() - new Date(state.lastShownAt).getTime();
    if (elapsed < POPUP_COOLDOWN_MS) {
      return false;
    }
  }
  return true;
}

/** Reads current state for a banner and returns whether it is eligible to be shown. */
export function isBannerPopupEligible(id: string): boolean {
  return isPopupEligible(readPopupState(id));
}

interface PopupBannerModalProps {
  banner: AdvertiseBanner;
  onDismiss: () => void;
}

const PopupBannerModal: React.FC<PopupBannerModalProps> = ({
  banner,
  onDismiss,
}) => {
  // Record this view on mount.
  useEffect(() => {
    const current = readPopupState(banner.id);
    writePopupState(banner.id, {
      viewCount: current.viewCount + 1,
      lastShownAt: new Date().toISOString(),
    });
  }, [banner.id]);

  const handleDismiss = () => {
    onDismiss();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Annuncio in evidenza"
      onClick={handleDismiss}
    >
      <div
        className="relative mx-4 max-w-lg w-full rounded-2xl bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 transition-colors"
          aria-label="Chiudi"
          onClick={handleDismiss}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* HTML content */}
        <div
          className="p-6 prose prose-sm max-w-none"
          // Banner description is authored by admins in a trusted admin panel — not user input.
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: banner.description ?? "" }}
        />
      </div>
    </div>
  );
};

export default PopupBannerModal;
